import {
  BitkitAlert,
  BitkitCodeSnippet,
  BitkitLabel,
  BitkitLabeledData,
  BitkitLabeledDataProps,
  rem,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { ReactNode, useContext } from 'react';

import Clock from '@/components/Clock/Clock';
import { GlobalPropsContext, useAccountFeatureFlag } from '@/components/page/GlobalPropsContext';
import { PublicApiBuild, TriggerTypes } from '@/models/Build';
import { GlobalProps } from '@/models/GlobalProps';
import { MachineType } from '@/models/MachineType';
import { Stack } from '@/models/Stack';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';

import BuildLogSummary from '../../types/BuildLogSummary';
import { buildMapperInstance } from '../../utils/BuildDetailsDataMapper';
import formatTimeBeforeBuild from '../../utils/formatTimeBeforeBuild';
import formatTimestamp, { parseTimestamp } from '../../utils/formatTimestamp';
import BuildPriority from '../BuildPriority/BuildPriority';
import MachineAndStack from '../MachineAndStack/MachineAndStack';

const UTILITY_BUILDS_DOCS_URL = 'https://docs.bitrise.io/en/bitrise-ci/run-and-analyze-builds/utility-builds';

type ConfigurationLastModifiedProps = Omit<BitkitLabeledDataProps, 'label'> & {
  build: PublicApiBuild;
};

const ConfigurationLastModified = ({ build, ...rest }: ConfigurationLastModifiedProps) => {
  const savedAt = parseTimestamp(build.configDatastoreUpdatedAt);
  const triggeredAt = parseTimestamp(build.triggeredAt);

  if (!savedAt || !triggeredAt) {
    return null;
  }

  return (
    <BitkitLabeledData
      label="Configuration last modified"
      value={formatTimestamp(savedAt)}
      subtext={formatTimeBeforeBuild(savedAt, triggeredAt)}
      tooltip="Configuration last saved before build was triggered."
      {...rest}
    />
  );
};

type MobileLabeledDataListProps = {
  abortReason?: string;
  build: PublicApiBuild;
  duration: ReactNode;
  creditCost?: string;
  machineType?: MachineType;
  stack: Stack;
  showStructuredBuildLog: boolean;
  showUtilityBuild: boolean;
  summary?: BuildLogSummary;
  isSummaryLoading: boolean;
  isSummaryDeletedOrError: boolean;
  stackRollbackVersion?: string;
};

const MobileLabeledDataList = ({
  abortReason,
  build,
  duration,
  creditCost,
  machineType,
  stack,
  showStructuredBuildLog,
  showUtilityBuild,
  summary,
  isSummaryLoading,
  isSummaryDeletedOrError,
  stackRollbackVersion,
}: MobileLabeledDataListProps) => {
  return (
    <>
      {abortReason && <BitkitLabeledData label="Abort reason" value={abortReason} />}

      <Box display="flex" flexDirection="column">
        <BitkitLabel>Workflow</BitkitLabel>
        <Box
          as="dd"
          display="flex"
          alignItems="center"
          color="text/body"
          textStyle="comp/data/sm"
          wordBreak="break-all"
        >
          {build.triggeredWorkflow}
        </Box>
      </Box>

      <Box display="flex" flexDirection="column">
        <BitkitLabel>Duration</BitkitLabel>
        <Box as="dd" display="flex" alignItems="center" color="text/body" textStyle="comp/data/sm">
          {duration}
        </Box>
      </Box>
      {creditCost && <BitkitLabeledData label="Credit cost" value={creditCost} />}
      <Box display="flex" flexDirection="column">
        <BitkitLabel>Stack and machine type</BitkitLabel>
        <MachineAndStack machineType={machineType} stack={stack} stackRollbackVersion={stackRollbackVersion} />
      </Box>
      <BuildPriority build={build} />
      <BitkitLabeledData
        label="Triggered at"
        value={buildMapperInstance.getTriggeredAt(build)}
        subtext={buildMapperInstance.getTriggeredBy(build)}
        wordBreak="break-all"
        maxWidth="100%"
        gridColumnStart="span 1"
        overflow="hidden"
      />
      <BitkitLabeledData label="Started at" value={buildMapperInstance.getStartedAt(build)} />
      <BitkitLabeledData label="Finished at" value={buildMapperInstance.getFinishedAt(build)} />
      {showUtilityBuild && (
        <BitkitLabeledData
          label="Utility build"
          value={buildMapperInstance.getUtilityBuildStatus(build)}
          tooltip={buildMapperInstance.getUtilityBuildTooltip(build)}
          learnMoreUrl={UTILITY_BUILDS_DOCS_URL}
          learnMoreTarget="_blank"
        />
      )}
      <ConfigurationLastModified build={build} wordBreak="break-all" maxWidth="100%" overflow="hidden" />
      {showStructuredBuildLog && !isSummaryDeletedOrError && (
        <>
          <BitkitLabeledData
            label="Bitrise version"
            value={summary?.cliInfo?.bitriseCliVersion}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
          <BitkitLabeledData
            label="Agent version"
            value={summary?.agentInfo?.denAgentVersion}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
          <BitkitLabeledData
            label="Machine region"
            value={summary?.agentInfo?.region}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
          <BitkitLabeledData
            label="Stack image version"
            value={summary?.agentInfo?.stackImageVersion}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
          <BitkitLabeledData
            label="Machine type ID"
            value={summary?.agentInfo?.machineType}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
          <BitkitLabeledData
            label="Machine class ID"
            value={summary?.agentInfo?.machineResourceClass}
            state={isSummaryLoading ? 'skeleton' : undefined}
          />
        </>
      )}
    </>
  );
};

type DesktopLabeledDataListProps = {
  build: PublicApiBuild;
  showStructuredBuildLog: boolean;
  showUtilityBuild: boolean;
  summary?: BuildLogSummary;
  isSummaryLoading: boolean;
  isSummaryDeletedOrError: boolean;
};

const DesktopLabeledDataList = ({
  build,
  showStructuredBuildLog,
  showUtilityBuild,
  summary,
  isSummaryLoading,
  isSummaryDeletedOrError,
}: DesktopLabeledDataListProps) => {
  const showLogInfo = showStructuredBuildLog && !isSummaryDeletedOrError;
  const loadingState = isSummaryLoading ? 'skeleton' : undefined;

  // Cells flow row-first into the 3-column grid, so when only the timestamp column is present (e.g. logs
  // deleted) the timestamps spread across the row instead of stacking in column 1. Each row renders its
  // version and machine cells together (not individually gated) so a row never has a partial set of cells,
  // which would let a later cell slide into the wrong column.
  return (
    <>
      {/* Row 1: Triggered at | Bitrise version | Machine region */}
      <BitkitLabeledData
        label="Triggered at"
        value={buildMapperInstance.getTriggeredAt(build)}
        subtext={buildMapperInstance.getTriggeredBy(build)}
        wordBreak="break-all"
        overflow="hidden"
      />
      {showLogInfo && (
        <>
          <BitkitLabeledData
            label="Bitrise version"
            value={summary?.cliInfo?.bitriseCliVersion}
            state={loadingState}
            overflow="hidden"
          />
          <BitkitLabeledData
            label="Machine region"
            value={summary?.agentInfo?.region}
            state={loadingState}
            overflow="hidden"
          />
        </>
      )}

      {/* Row 2: Started at | Agent version | Machine type ID */}
      <BitkitLabeledData label="Started at" value={buildMapperInstance.getStartedAt(build)} overflow="hidden" />
      {showLogInfo && (
        <>
          <BitkitLabeledData
            label="Agent version"
            value={summary?.agentInfo?.denAgentVersion}
            state={loadingState}
            overflow="hidden"
          />
          <BitkitLabeledData
            label="Machine type ID"
            value={summary?.agentInfo?.machineType}
            state={loadingState}
            overflow="hidden"
          />
        </>
      )}

      {/* Row 3: Finished at | Stack image version | Machine class ID */}
      <BitkitLabeledData label="Finished at" value={buildMapperInstance.getFinishedAt(build)} overflow="hidden" />
      {showLogInfo && (
        <>
          <BitkitLabeledData
            label="Stack image version"
            value={summary?.agentInfo?.stackImageVersion}
            state={loadingState}
            overflow="hidden"
          />
          <BitkitLabeledData
            label="Machine class ID"
            value={summary?.agentInfo?.machineResourceClass}
            state={loadingState}
            overflow="hidden"
          />
        </>
      )}

      {/* Rows 4+: one cell per row in column 1, under Finished at */}
      {showUtilityBuild && (
        <BitkitLabeledData
          label="Utility build"
          value={buildMapperInstance.getUtilityBuildStatus(build)}
          tooltip={buildMapperInstance.getUtilityBuildTooltip(build)}
          learnMoreUrl={UTILITY_BUILDS_DOCS_URL}
          learnMoreTarget="_blank"
          gridColumnStart={1}
          overflow="hidden"
        />
      )}
      <ConfigurationLastModified build={build} gridColumnStart={1} overflow="hidden" />
    </>
  );
};

type DetailsProps = {
  showStructuredBuildLog: boolean;
};

const Details = ({ showStructuredBuildLog }: DetailsProps) => {
  const { account } = useContext(GlobalPropsContext) as GlobalProps;
  const utilityBuildsEntitled = useAccountFeatureFlag('utilityBuildsEntitled');

  const { build, stack, machineType, logSummary, logSummaryStatus } = useBuildDetailsPageStore();

  // A utility build says so on every plan; only an entitled workspace is also told when a
  // build did not qualify.
  const showUtilityBuild = build.utility || utilityBuildsEntitled;

  const isSummaryLoading = logSummaryStatus === 'pending' || logSummaryStatus === 'fetching';
  const isSummaryDeleted = logSummaryStatus === 'deleted';
  const isSummaryError = logSummaryStatus === 'error';

  const { isMobile } = useResponsive();
  const abortReason = buildMapperInstance.getAbortReason(build);
  const durationData = buildMapperInstance.getDuration(build);
  const duration =
    typeof durationData === 'string' ? (
      <>{durationData}</>
    ) : (
      <>{durationData.start && <Clock startDate={durationData.start} endDate={durationData.end} />}</>
    );
  const creditCost = account?.isCreditBasedPlan ? buildMapperInstance.getCreditCost(build) : undefined;

  return (
    <Box columnGap={[null, '32']} display={[null, 'grid']} gridTemplateColumns={[null, '2fr 1fr']}>
      <Box>
        {isSummaryDeleted && (
          <BitkitAlert
            variant="info"
            messageText="The logs for this build were deleted, so some of its details are no longer available."
            marginBottom="24"
          />
        )}
        {isSummaryError && (
          <BitkitAlert
            variant="critical"
            messageText="Failed to load some of the build's details. Please try reloading the page."
            marginBottom="24"
          />
        )}
        <Box
          display="grid"
          rowGap="24"
          columnGap="32"
          gridTemplateColumns={isMobile ? '1fr' : 'repeat(3, 1fr)'}
          alignItems="start"
        >
          {isMobile ? (
            <MobileLabeledDataList
              abortReason={abortReason}
              build={build}
              duration={duration}
              creditCost={creditCost}
              machineType={machineType}
              stack={stack}
              showStructuredBuildLog={showStructuredBuildLog}
              showUtilityBuild={showUtilityBuild}
              summary={logSummary}
              isSummaryLoading={isSummaryLoading}
              isSummaryDeletedOrError={isSummaryDeleted || isSummaryError}
              stackRollbackVersion={build.stackRollbackVersion}
            />
          ) : (
            <DesktopLabeledDataList
              build={build}
              showStructuredBuildLog={showStructuredBuildLog}
              showUtilityBuild={showUtilityBuild}
              summary={logSummary}
              isSummaryLoading={isSummaryLoading}
              isSummaryDeletedOrError={isSummaryDeleted || isSummaryError}
            />
          )}
        </Box>
        <Box display="flex" flexDirection="column" rowGap="24" marginBlockStart="24" maxWidth={rem(1024)}>
          {showStructuredBuildLog && !isSummaryDeleted && !isSummaryError && (
            <Box display="flex" flexDirection="column" gap="4">
              <BitkitLabel>Hostname</BitkitLabel>
              {isSummaryLoading ? (
                <Skeleton height="24" />
              ) : (
                <BitkitCodeSnippet variant="single">{logSummary?.agentInfo?.hostName ?? ''}</BitkitCodeSnippet>
              )}
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap="4">
            <BitkitLabel>
              {buildMapperInstance.getTriggerType(build) === TriggerTypes.PULL_REQUEST
                ? 'Pull request description'
                : 'Commit message'}
            </BitkitLabel>
            {buildMapperInstance.hasTriggerData(build) ? (
              <BitkitCodeSnippet variant="multi" startingHeight={80}>
                {buildMapperInstance.getTriggerData(build)}
              </BitkitCodeSnippet>
            ) : (
              <BitkitCodeSnippet variant="single">{buildMapperInstance.getTriggerData(build)}</BitkitCodeSnippet>
            )}
          </Box>
          <Box display="flex" flexDirection="column" gap="4">
            <BitkitLabel>Parameters</BitkitLabel>
            <BitkitCodeSnippet variant="multi" startingHeight={80}>
              {buildMapperInstance.getParameters(build)}
            </BitkitCodeSnippet>
          </Box>
          {build.denTags && (
            <Box display="flex" flexDirection="column" gap="4">
              <BitkitLabel>Request tags</BitkitLabel>
              <BitkitCodeSnippet variant="multi">{JSON.stringify(build.denTags)}</BitkitCodeSnippet>
            </Box>
          )}
          {showStructuredBuildLog && !isSummaryDeleted && !isSummaryError && (
            <>
              <Box display="flex" flexDirection="column" gap="4">
                <BitkitLabel>Command</BitkitLabel>
                {isSummaryLoading ? (
                  <Skeleton height="24" />
                ) : (
                  <BitkitCodeSnippet variant="multi" startingHeight={80}>
                    {logSummary?.agentInfo?.bitriseCliCommand ?? ''}
                  </BitkitCodeSnippet>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap="4" maxWidth="100%">
                <BitkitLabel>Node</BitkitLabel>
                {isSummaryLoading ? (
                  <Skeleton height="24" />
                ) : (
                  <BitkitCodeSnippet variant="single">{logSummary?.agentInfo?.nodeName ?? ''}</BitkitCodeSnippet>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Details;
